import React, { useState, useRef, useCallback } from 'react';
import { Box, IconButton, Popover, Typography } from '@mui/material';
import SettingsIcon from '@mui/icons-material/SettingsOutlined';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FIELD_CATEGORIES } from './fieldTypes';
import { FieldTypeGrid, fieldTypePopoverProps } from './AddFieldButton';
import FieldSettingsPanel from './FieldSettingsPanel';
import { PublicDesignFieldBody } from './FieldBodyPreview';
import PublicFieldLayout from './PublicFieldLayout';
import ClassicFieldLayout from './ClassicFieldLayout';
import { TipTapEditor } from '../../../TipTap';

/**
 * Drag/settings/add/duplicate/delete chrome shared by the classic bordered-card
 * builder and the Tally-style public design surface. This component owns all
 * field state and the shared control/settings elements, then delegates the
 * actual arrangement to ClassicFieldLayout or PublicFieldLayout.
 */
export default function WysiwygFieldBlock({
  field,
  index,
  sortableId,
  onUpdate,
  onRemove,
  onDuplicate,
  onAddField,
  theme,
  palette,
  availablePlaceholders,
  variant = 'classic',
}) {
  const [settingsExpanded, setSettingsExpanded] = useState(false);
  const [addMenuAnchor, setAddMenuAnchor] = useState(null);
  const [hovered, setHovered] = useState(false);
  const leaveTimerRef = useRef(null);
  const addMenuOpen = Boolean(addMenuAnchor);
  const isDisplayField = field.type === 'display';
  const isPublic = variant === 'public';

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: sortableId || field._id || field.key });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
  };

  const handleFieldUpdate = (updates) => onUpdate(index, updates);

  const handleAddSelect = (type) => {
    onAddField(type, index + 1);
    setAddMenuAnchor(null);
  };

  const handleMouseEnter = useCallback(() => {
    if (leaveTimerRef.current) {
      clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = null;
    }
    setHovered(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    leaveTimerRef.current = setTimeout(() => {
      setHovered(false);
    }, 300);
  }, []);

  const handleValidationsChange = useCallback(
    (newValidations) => onUpdate(index, { validations: newValidations }),
    [index, onUpdate]
  );

  const showControls = hovered || isDragging || settingsExpanded || addMenuOpen;
  const validations = field.validations || {};

  const controlButtons = (
    <>
      <IconButton
        size="small"
        onClick={(e) => { e.stopPropagation(); setSettingsExpanded((v) => !v); }}
        sx={{
          p: '3px',
          color: settingsExpanded ? palette.textPrimary : palette.textSecondary,
          backgroundColor: settingsExpanded ? palette.border : 'transparent',
          borderRadius: '4px',
        }}
        title="Settings"
      >
        <SettingsIcon sx={{ width: 15, height: 15 }} />
      </IconButton>
      <IconButton
        size="small"
        onClick={(e) => { e.stopPropagation(); setAddMenuAnchor(e.currentTarget); }}
        sx={{ p: '3px', color: palette.textSecondary }}
        title="Add field below"
      >
        <AddIcon sx={{ width: 15, height: 15 }} />
      </IconButton>
      <IconButton
        size="small"
        onClick={(e) => { e.stopPropagation(); onDuplicate(index); }}
        sx={{ p: '3px', color: palette.textSecondary }}
        title="Duplicate"
      >
        <ContentCopyIcon sx={{ width: 14, height: 14 }} />
      </IconButton>
      <IconButton
        size="small"
        onClick={(e) => { e.stopPropagation(); onRemove(index); }}
        sx={{ p: '3px', color: palette.textSecondary, '&:hover': { color: palette.errorText || '#b91c1c' } }}
        title="Delete"
      >
        <DeleteOutlineIcon sx={{ width: 15, height: 15 }} />
      </IconButton>
    </>
  );

  const settingsPanel = settingsExpanded ? (
    <FieldSettingsPanel
      field={field}
      index={index}
      onUpdate={onUpdate}
      isDisplayField={isDisplayField}
      isPublic={isPublic}
      palette={palette}
      theme={theme}
      validations={validations}
      onValidationsChange={handleValidationsChange}
    />
  ) : null;

  const sharedLayoutProps = {
    setNodeRef,
    style,
    attributes,
    listeners,
    isDragging,
    showControls,
    palette,
    isDisplayField,
    controlButtons,
    settingsPanel,
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
  };

  return (
    <>
      {isPublic ? (
        <PublicFieldLayout
          {...sharedLayoutProps}
          helpText={field.helpText}
          labelEditor={
            !isDisplayField && (
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mb: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0, fontSize: '14px', fontWeight: 500, color: palette.textPrimary }}>
                  <TipTapEditor
                    value={field.label || ''}
                    onChange={(val) => handleFieldUpdate({ label: val })}
                    showToolbar={false}
                    stripHtml={true}
                    availablePlaceholders={availablePlaceholders}
                    placeholder="Type your question..."
                    minHeight="auto"
                    className="tiptap-inline"
                  />
                </Box>
                {field.required && (
                  <Typography sx={{ fontSize: '12px', color: palette.textSecondary, flexShrink: 0, fontWeight: 400 }}>
                    (required)
                  </Typography>
                )}
              </Box>
            )
          }
          fieldBody={
            <PublicDesignFieldBody
              field={field}
              onUpdate={handleFieldUpdate}
              palette={palette}
              theme={theme}
              availablePlaceholders={availablePlaceholders}
            />
          }
        />
      ) : (
        <ClassicFieldLayout
          {...sharedLayoutProps}
          theme={theme}
          field={field}
          availablePlaceholders={availablePlaceholders}
          onFieldUpdate={handleFieldUpdate}
        />
      )}

      <Popover open={addMenuOpen} anchorEl={addMenuAnchor} onClose={() => setAddMenuAnchor(null)} {...fieldTypePopoverProps}>
        <FieldTypeGrid categories={FIELD_CATEGORIES} onSelect={handleAddSelect} />
      </Popover>
    </>
  );
}
